// Anonymous public forum POST detail page — fully indexable.
//
// /forum/:slug → fetches /api/public/forum/posts/{slug} → renders the
// question + all replies. Sets <title>, <meta description>, and
// schema.org JSON-LD QAPage structured data so Google can show the
// question/answer pair as a rich snippet in search results.
//
// CTA at the bottom: sign up to reply.
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ArrowLeft, ArrowRight, MessageCircle, ChevronUp, CheckCircle2, Trophy,
} from "lucide-react";
import { metaFor, timeAgo } from "@/lib/forumCategories";

const backend = process.env.REACT_APP_BACKEND_URL;

function setMeta(name, content, property = false) {
  const sel = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
  let el = document.querySelector(sel);
  if (!el) {
    el = document.createElement("meta");
    if (property) el.setAttribute("property", name);
    else el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function injectJsonLd(json, id = "forum-qapage-jsonld") {
  // Remove any previous one we injected so navigation doesn't stack tags.
  const prev = document.getElementById(id);
  if (prev) prev.remove();
  const tag = document.createElement("script");
  tag.type = "application/ld+json";
  tag.id = id;
  tag.text = JSON.stringify(json);
  document.head.appendChild(tag);
}

export default function PublicForumPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${backend}/api/public/forum/posts/${slug}`)
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((data) => {
        setPost(data);

        // SEO: title + meta + Schema.org QAPage JSON-LD.
        const title = `${data.title} — iSpeedToClose Forum`;
        document.title = title.slice(0, 200);
        const desc = (data.body || "").replace(/[#*_`>\-]/g, "").slice(0, 160);
        setMeta("description", desc);
        setMeta("og:title", title, true);
        setMeta("og:description", desc, true);

        const topLevel = (data.replies || []).filter((r) => !r.parent_reply_id);
        injectJsonLd({
          "@context": "https://schema.org",
          "@type": "QAPage",
          mainEntity: {
            "@type": "Question",
            name: data.title,
            text: data.body,
            answerCount: data.reply_count || 0,
            dateCreated: data.created_at,
            author: { "@type": "Person", name: data.author?.display_name || "anon" },
            ...(data.best_reply_id && topLevel.find((r) => r.id === data.best_reply_id) ? {
              acceptedAnswer: {
                "@type": "Answer",
                text: topLevel.find((r) => r.id === data.best_reply_id).body,
                upvoteCount: topLevel.find((r) => r.id === data.best_reply_id).score || 0,
                author: {
                  "@type": "Person",
                  name: topLevel.find((r) => r.id === data.best_reply_id).author?.display_name || "anon",
                },
              },
            } : {}),
            suggestedAnswer: topLevel.slice(0, 5).map((r) => ({
              "@type": "Answer",
              text: r.body,
              upvoteCount: r.score || 0,
              dateCreated: r.created_at,
              author: { "@type": "Person", name: r.author?.display_name || "anon" },
            })),
          },
        });
      })
      .catch((status) => setError(status === 404 ? "Post not found" : "Couldn't load"));
    return () => {
      const prev = document.getElementById("forum-qapage-jsonld");
      if (prev) prev.remove();
    };
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center text-center px-6">
        <div>
          <div className="text-zinc-700 font-medium">{error}</div>
          <Link to="/forum" className="text-sm text-zinc-500 underline mt-2 inline-block">← Back to Forum</Link>
        </div>
      </div>
    );
  }
  if (!post) {
    return <div className="min-h-screen bg-zinc-50" />;
  }

  const meta = metaFor(post.category);
  const Icon = meta.icon;
  const topLevel = (post.replies || []).filter((r) => !r.parent_reply_id);
  const childrenOf = (id) => (post.replies || []).filter((r) => r.parent_reply_id === id);
  const isBest = (rid) => post.best_reply_id === rid;

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="public-forum-post">
      <header className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/forum" className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1">
            <ArrowLeft size={12} /> Back to Forum
          </Link>
          <Link
            to="/register"
            className="inline-flex items-center gap-1 bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-bold text-xs px-3 py-1.5 rounded-full"
          >
            Sign up <ArrowRight size={11} />
          </Link>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 py-10">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <span
            className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border"
            style={{ borderColor: meta.accent + "55", color: meta.accent, background: meta.accent + "10" }}
          >
            <Icon size={11} /> {(post.category || "").replace(/-/g, " ")}
          </span>
          {post.is_solved && (
            <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 font-bold uppercase tracking-wide">
              <CheckCircle2 size={11} /> Solved
            </span>
          )}
        </div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-zinc-900 leading-tight mb-3">
          {post.title}
        </h1>
        <div className="text-xs text-zinc-500 flex items-center gap-2 mb-6">
          <span className="font-medium text-zinc-700">{post.author?.display_name}</span>
          {post.author?.reputation > 0 && (
            <span className="inline-flex items-center gap-1 text-amber-700"><Trophy size={10} /> {post.author.reputation} rep</span>
          )}
          <span>· {timeAgo(post.created_at)}</span>
          <span>· {post.view_count || 0} view{post.view_count === 1 ? "" : "s"}</span>
        </div>

        <div className="prose prose-zinc max-w-none bg-white p-6 rounded-lg border" data-testid="public-post-body">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.body || ""}</ReactMarkdown>
        </div>

        <h2 className="mt-10 mb-3 text-base font-bold text-zinc-900">
          {post.reply_count || 0} {post.reply_count === 1 ? "Reply" : "Replies"}
        </h2>

        <div className="space-y-3" data-testid="public-replies-list">
          {topLevel.length === 0 && (
            <div className="bg-white border rounded-lg p-6 text-center text-sm text-zinc-500">
              No replies yet.
            </div>
          )}
          {topLevel.map((r) => (
            <div key={r.id}>
              <div
                className={
                  "bg-white border rounded-lg p-5 " +
                  (isBest(r.id) ? "border-l-4 border-l-emerald-500" : "")
                }
                data-testid={`public-reply-${r.id}`}
              >
                {isBest(r.id) && (
                  <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-emerald-700 mb-2 px-2 py-0.5 rounded-full bg-emerald-100 border border-emerald-200">
                    <CheckCircle2 size={11} /> Best answer
                  </div>
                )}
                <div className="flex gap-3">
                  <div className="flex flex-col items-center text-zinc-500 w-10 flex-shrink-0">
                    <ChevronUp size={14} />
                    <div className="text-sm font-bold text-zinc-700">{r.score || 0}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-zinc-500 mb-2">
                      <span className="font-medium text-zinc-700">{r.author?.display_name}</span>
                      {r.author?.reputation > 0 && (
                        <span className="ml-2 inline-flex items-center gap-1 text-amber-700"><Trophy size={10} /> {r.author.reputation}</span>
                      )}
                      <span> · {timeAgo(r.created_at)}</span>
                    </div>
                    <div className="prose prose-sm max-w-none text-zinc-800">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{r.body || ""}</ReactMarkdown>
                    </div>
                  </div>
                </div>
              </div>
              {childrenOf(r.id).map((c) => (
                <div key={c.id} className="ml-10 mt-2 bg-white border rounded-lg p-4">
                  <div className="flex gap-3">
                    <div className="flex flex-col items-center text-zinc-500 w-10 flex-shrink-0">
                      <ChevronUp size={12} />
                      <div className="text-xs font-bold text-zinc-700">{c.score || 0}</div>
                    </div>
                    <div className="flex-1">
                      <div className="text-xs text-zinc-500 mb-1.5">
                        <span className="font-medium text-zinc-700">{c.author?.display_name}</span>
                        <span> · {timeAgo(c.created_at)}</span>
                      </div>
                      <div className="prose prose-sm max-w-none text-zinc-800">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{c.body || ""}</ReactMarkdown>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="mt-12 text-center bg-zinc-900 text-white rounded-2xl p-8">
          <h2 className="font-display font-bold text-2xl mb-2">Want to add your answer?</h2>
          <p className="text-white/70 text-sm mb-4">
            Sign up free to reply, ask, and earn community reputation.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-bold text-sm px-6 py-3 rounded-full"
            data-testid="public-post-signup-cta"
          >
            Start free <ArrowRight size={14} />
          </Link>
        </div>

        <div className="mt-8 text-center">
          <Link to="/forum" className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1">
            <MessageCircle size={11} /> Browse more questions
          </Link>
        </div>
      </article>
    </div>
  );
}
